import React from 'react';
import { Chart } from 'react-google-charts';
import { Card, CardBody, CardHeader } from '../../components/Bootstrap';
import { NoDataAvailable } from '../../components';

interface LiveSalesProps {
  acceptedSales: number;
  nonAcceptedSales: number;
  preorderSales: number;
}

interface LiveSalesState {
  acceptedSalesStyle: any;
  nonAcceptedSalesStyle: any;
  preorderSalesStyle: any;
  hasReceivedPositions: boolean;
}

const COLOR_MAP = {
  accepted: '#bc74d1',
  nonAccepted: '#667ab3',
  preOrders: '#303487',
};

const toRadians = (degrees) => {
  return (degrees * Math.PI) / 180;
};

const transformForDegree = (degree) => {
  const translate = {
    x: Math.round(Math.cos(toRadians(degree)) * 100),
    y: Math.round(Math.sin(toRadians(degree)) * 100),
  };

  return `translate(${translate.x}px, ${translate.y}px)`;
};

const Label = ({ style, sales }) => {
  return (
    <div className='live-sales-label-wrapper' style={style}>
      <div className='live-sales-label'>${sales}</div>
    </div>
  );
};

export class LiveSales extends React.Component<LiveSalesProps, LiveSalesState> {
  constructor(props) {
    super(props);

    this.state = {
      acceptedSalesStyle: null,
      nonAcceptedSalesStyle: null,
      preorderSalesStyle: null,
      hasReceivedPositions: false,
    };
  }

  renderLoadingMessage() {
    return <div>Loading...</div>;
  }

  graphOptionsForRevenue(rows) {
    const slices = {};
    let i = 0;
    if (this.props.acceptedSales) {
      slices[i] = { color: COLOR_MAP.accepted };
      i++;
    }

    if (this.props.nonAcceptedSales) {
      slices[i] = { color: COLOR_MAP.nonAccepted };
      i++;
    }

    if (this.props.preorderSales) {
      slices[i] = { color: COLOR_MAP.preOrders };
    }

    return {
      options: {
        legend: 'none',
        pieSliceText: 'none',
        pieStartAngle: 0,
        pieSliceBorderColor: 'transparent',
        tooltip: { trigger: 'none' },
        titlePosition: 'none',
        axisTitlesPosition: 'none',
        enableInteractivity: false,
        chartArea: {
          width: '100%',
          height: '100%',
        },
        pieHole: 0.75,
        backgroundColor: 'transparent',
        slices,
      },
      rows,
      columns: [
        {
          type: 'string',
          label: 'Name',
        },
        {
          type: 'number',
          label: 'Percentage',
        },
      ],
    };
  }

  readyCallback = () => {
    if (this.state.hasReceivedPositions) {
      return;
    }

    const { acceptedSales, nonAcceptedSales, preorderSales } = this.props;

    let acceptedSalesStyle: any = { display: 'none' };
    let nonAcceptedSalesStyle: any = { display: 'none' };
    let preorderSalesStyle: any = { display: 'none' };

    let startingDegree = -90;
    const total = (acceptedSales || 0) + (nonAcceptedSales || 0) + (preorderSales || 0);

    if (acceptedSales) {
      const arc = (acceptedSales / total) * 360;
      const degree = startingDegree + arc / 2;
      acceptedSalesStyle = { transform: transformForDegree(degree) };
      startingDegree += arc;
    }

    if (nonAcceptedSales) {
      const arc = (nonAcceptedSales / total) * 360;
      const degree = startingDegree + arc / 2;
      nonAcceptedSalesStyle = { transform: transformForDegree(degree) };
      startingDegree += arc;
    }

    if (preorderSales) {
      const arc = (preorderSales / total) * 360;
      const degree = startingDegree + arc / 2;
      preorderSalesStyle = { transform: transformForDegree(degree) };
      startingDegree += arc;
    }

    this.setState({
      hasReceivedPositions: true,
      acceptedSalesStyle,
      nonAcceptedSalesStyle,
      preorderSalesStyle,
    });
  };

  render() {
    if (!this.props.acceptedSales && !this.props.nonAcceptedSales && !this.props.preorderSales) {
      return (
        <Card className='h-100 live-sales'>
          <CardHeader>Live Sales</CardHeader>
          <CardBody className='live-sales-body'>
            <NoDataAvailable className='text-md-left' />
          </CardBody>
        </Card>
      );
    }

    const rows = [
      ['Accepted', this.props.acceptedSales],
      ['Non Accepted', this.props.nonAcceptedSales],
      ['Preorders', this.props.preorderSales],
    ].filter((x) => x[1]);

    const options = this.graphOptionsForRevenue(rows);

    const props: any = {
      chartType: 'PieChart',
      loader: this.renderLoadingMessage(),
      rows: options.rows,
      columns: options.columns,
      options: options.options,
      graph_id: `live-sales-${this.props.acceptedSales}`,
      width: '150px',
      height: '150px',
      legend_toggle: true,
      chartEvents: [
        {
          eventName: 'ready',
          callback: this.readyCallback,
        },
      ],
    };

    const { acceptedSalesStyle, nonAcceptedSalesStyle, preorderSalesStyle } = this.state;

    return (
      <Card className='h-100 live-sales'>
        <CardHeader>Live Sales</CardHeader>
        <CardBody className='live-sales-body'>
          <div className='live-sales-body-wrapper'>
            <Chart {...props} />
            <Label sales={this.props.acceptedSales} style={acceptedSalesStyle} />
            <Label sales={this.props.nonAcceptedSales} style={nonAcceptedSalesStyle} />
            <Label sales={this.props.preorderSales} style={preorderSalesStyle} />
          </div>
          <div className='live-sales-key'>
            <div className='live-sales-key-block-wrapper'>
              <div className='live-sales-key-block' style={{ background: COLOR_MAP.accepted }} /> Accepted
            </div>
            <div className='live-sales-key-block-wrapper'>
              <div className='live-sales-key-block' style={{ background: COLOR_MAP.nonAccepted }} /> Non-Accepted
            </div>
            <div className='live-sales-key-block-wrapper'>
              <div className='live-sales-key-block' style={{ background: COLOR_MAP.preOrders }} /> Pre-Orders
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }
}
